import React, { useState } from 'react';
import api from '../services/api';

const ENTITIES = [
  { id: 'products', label: 'Products' },
  { id: 'orders', label: 'Orders' },
  { id: 'customers', label: 'Customers' },
  { id: 'vendors', label: 'Vendors' },
  { id: 'categories', label: 'Categories' },
  { id: 'inventory', label: 'Inventory' },
];

const IMPORT_ENTITIES = [
  { id: 'products', label: 'Products', hint: 'sku, title, description, category, master_price, master_stock, barcode' },
  { id: 'customers', label: 'Customers', hint: 'name, email, phone, gstin, address, city, state, pincode, status' },
  { id: 'vendors', label: 'Vendors', hint: 'name, contact_person, email, phone, gstin, address, city, state, pincode, payment_terms, status' },
];

const ExportImport = () => {
  const [tab, setTab] = useState('export');
  const [exportType, setExportType] = useState('products');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const [importType, setImportType] = useState('products');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportError('');
      const token = localStorage.getItem('token');
      const res = await fetch(`/backend/public/api/tools/export.php?type=${exportType}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) { setExportError('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setExportError('Failed to export'); }
    finally { setExporting(false); }
  };

  const handleImport = async () => {
    if (!file) { setImportError('Please select a CSV file'); return; }
    try {
      setImporting(true);
      setImportError('');
      setImportResult(null);
      const fd = new FormData();
      fd.append('type', importType);
      fd.append('file', file);
      const res = await api.post('/tools/import.php', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) setImportResult(res.data.data);
      else setImportError(res.data.message);
    } catch (e) { setImportError('Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Export / Import</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Export data to CSV or import records from CSV files</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex">
            <button onClick={() => { setTab('export'); setExportError(''); }} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'export' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Export</button>
            <button onClick={() => { setTab('import'); setImportError(''); setImportResult(null); }} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'import' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Import</button>
          </div>
        </div>

        {tab === 'export' ? (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select data to export</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {ENTITIES.map(e => (
                  <button key={e.id} onClick={() => setExportType(e.id)} className={`p-4 rounded-lg border-2 text-center transition-all ${exportType === e.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                    <p className="text-sm font-medium">{e.label}</p>
                  </button>
                ))}
              </div>
            </div>
            {exportError && <p className="text-red-600 text-sm">{exportError}</p>}
            <button onClick={handleExport} disabled={exporting} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors">
              {exporting ? 'Exporting...' : `Download ${ENTITIES.find(e => e.id === exportType)?.label} CSV`}
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Entity type</label>
              <select value={importType} onChange={e => { setImportType(e.target.value); setImportResult(null); setImportError(''); }} className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                {IMPORT_ENTITIES.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium mb-1">Required CSV format — first row must be headers:</p>
              <code className="text-xs">{IMPORT_ENTITIES.find(e => e.id === importType)?.hint}</code>
              <p className="mt-2 text-xs text-gray-500">First column must be the primary identifier (sku for products, name for customers/vendors). Other columns optional.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Upload CSV file</label>
              <input type="file" accept=".csv" onChange={e => { setFile(e.target.files[0]); setImportResult(null); setImportError(''); }} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
            </div>

            {importError && <p className="text-red-600 text-sm">{importError}</p>}

            {importResult && (
              <div className={`rounded-lg p-4 ${importResult.errors?.length ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <p className={`font-medium ${importResult.errors?.length ? 'text-yellow-800' : 'text-green-800}'}`}>
                  {importResult.imported} records imported
                  {importResult.total_errors > 0 && `, ${importResult.total_errors} errors`}
                </p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-sm text-red-600 space-y-1 max-h-40 overflow-y-auto">
                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}

            <button onClick={handleImport} disabled={importing || !file} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors">
              {importing ? 'Importing...' : 'Import CSV'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportImport;
